import classNames, { Argument } from "classnames/dedupe";
import React, {
  CSSProperties,
  ElementType,
  forwardRef,
  useContext,
} from "react";
import LinkContext from "../../Link/LinkContext";
import { TThemeVariant, useTheme } from "../../../Utility/ThemeProvider";

export type TLinkButtonProps = Omit<
  JSX.IntrinsicElements["button"],
  "type" | "ref"
> &
  TCustomLinkButtonProps & { [key: string]: unknown };

export type TCustomLinkButtonProps = {
  /**
   * Set Button variant, decorates component with css classes
   * @type TButtonType
   */
  variant?: TLinkButtonType;

  /**
   * HTML button type of input
   */
  type?: "button" | "submit" | "reset";

  /**
   * Additional css style rules set to button
   */
  style?: CSSProperties;

  /**
   * The custom set of css classes used to style component
   */
  classes?: {
    root?: Argument;
    iconRoot?: Argument;
    text?: Argument;
    content?: Argument;
  };

  /**
   * SVG Icon component
   *
   *@type {ReactElement}
   */
  startIcon?: ElementType;

  /**
   * SVG Icon component
   *
   *@type {ReactElement}
   */
  endIcon?: ElementType;

  /** Element to draw button as (for example, "a" tag).
   *  Has the "button" value by default */
  as?: ElementType;

  /** Optional href for Link ("<a>") representation */
  href?: string;

  target?: HTMLAnchorElement["target"];
  theme?: TThemeVariant;
};

export type TLinkButtonType = "default" | "bold" | "inline" | "icon" | "alert";

enum ClassMapping {
  bold = "button-link__bold",
  alert = "button-link--alert",
}

const LinkButton = forwardRef<HTMLButtonElement, TLinkButtonProps>(
  (
    {
      variant = "default",
      type,
      as: Tag,
      target,
      href,
      style,
      disabled,
      classes,
      theme: propsTheme,
      startIcon: StartIconComponent,
      endIcon: EndIconComponent,
      children,
      ...other
    },
    ref,
  ) => {
    const theme = useTheme(propsTheme);

    const rootClasses = classNames(
      "button-link",
      { "text-color-white": theme === "dark" },
      ClassMapping[variant],
      {
        "button-link--disabled": disabled,
      },
      classes?.root,
    );

    const wrapperClasses = classNames(
      "button-link--wrapper",
      {
        "button-link--alert": variant === "alert",
        "cursor-pointer": !disabled,
      },
      classes?.iconRoot,
    );

    const { wrapper: LinkCustomContextWrapper } = useContext(LinkContext);

    const RenderComponent = Tag || LinkCustomContextWrapper || "button";

    return (
      <RenderComponent
        className={rootClasses}
        style={style}
        type={type}
        ref={ref}
        href={href}
        target={target}
        disabled={disabled}
        data-testid="LinkButton"
        {...other}
      >
        {["icon", "alert"].includes(variant) ? (
          <div className={wrapperClasses}>
            {StartIconComponent && (
              <span className="me-4">
                <StartIconComponent />
              </span>
            )}
            <span className={classNames(classes?.content)}>{children}</span>
            {EndIconComponent && (
              <span className="ms-4">
                <EndIconComponent />
              </span>
            )}
          </div>
        ) : (
          <span className={classNames(classes?.content)}>{children}</span>
        )}
      </RenderComponent>
    );
  },
);

export default LinkButton;
